import httpClient from "../http/http-client";
import { ILibraryRuleDeleteResult } from '../library-rule/library-rule.model';
import {
  ILibraryQuestion,
  ILibraryQuestionGroup,
  ILibraryQuestionGroupPushResult,
  ILibraryQuestionGroupPushTarget,
  ILibraryImportClient,
  ILibraryImportResult,
  ILibraryRetractResult,
} from './library-question-group.model';

const BASE = 'partner/library';

export default class LibraryQuestionGroupService {
  http;

  constructor() {
    this.http = httpClient
  }

  // ---- groups ----------------------------------------------------------------

  async getAllWithPagination(page: number, perPage: number = 10, orderBy?: string, order?: 'asc' | 'desc', nameIncludes?: string): Promise<{ count: number, rows: ILibraryQuestionGroup[] }> {
    const params = new URLSearchParams({ page: String(page), perPage: String(perPage) });
    if (order && orderBy) { params.append('orderBy', orderBy); params.append('order', order); }
    if (nameIncludes) params.append('nameIncludes', nameIncludes);
    return this.http.get(`${BASE}/question-groups?${params.toString()}`)
      .then(r => r.data).catch(err => { console.log(err); throw err; });
  }

  async getOne(id: string): Promise<ILibraryQuestionGroup> {
    return this.http.get(`${BASE}/question-groups/${id}`)
      .then(r => r.data).catch(err => { console.log(err); throw err; });
  }

  async create(group: Partial<ILibraryQuestionGroup>): Promise<ILibraryQuestionGroup> {
    return this.http.post(`${BASE}/question-groups`, group)
      .then(r => r.data).catch(err => { console.log(err); throw err; });
  }

  async edit(id: string, group: Partial<ILibraryQuestionGroup>): Promise<ILibraryQuestionGroup> {
    return this.http.patch(`${BASE}/question-groups/${id}`, group)
      .then(r => r.data).catch(err => { console.log(err); throw err; });
  }

  // removeFromClients also takes the survey back out of the client accounts it reached. A
  // group a client has already used is released to them rather than deleted.
  async delete(id: string, removeFromClients: boolean = false): Promise<ILibraryRuleDeleteResult> {
    return this.http.delete(`${BASE}/question-groups/${id}`, { data: { removeFromClients } })
      .then(r => r.data).catch(err => { console.log(err); throw err; });
  }

  // ---- pulling a client's own survey INTO the library -------------------------

  async getImportableGroups(): Promise<{ clients: ILibraryImportClient[] }> {
    return this.http.get(`${BASE}/question-groups/importable`)
      .then(r => r.data).catch(err => { console.log(err); throw err; });
  }

  // Copies the client's survey up into the library AND links their rows to it, so it reads as
  // already pushed and up to date rather than as something they now hold twice.
  async importFromClient(clientOrganisationId: string, surveyQuestionGroupId: string, name: string): Promise<ILibraryImportResult> {
    return this.http.post(`${BASE}/question-groups/import`, { clientOrganisationId, surveyQuestionGroupId, name })
      .then(r => r.data).catch(err => { console.log(err); throw err; });
  }

  async getPushTargets(id: string): Promise<{ clients: ILibraryQuestionGroupPushTarget[], integrityProblems: any[] }> {
    return this.http.get(`${BASE}/question-groups/${id}/targets`)
      .then(r => r.data).catch(err => { console.log(err); throw err; });
  }

  async push(id: string, clientOrganisationIds: string[], overwriteClientEditIds: string[] = []): Promise<ILibraryQuestionGroupPushResult> {
    return this.http.post(`${BASE}/question-groups/${id}/push`, { clientOrganisationIds, overwriteClientEditIds })
      .then(r => r.data).catch(err => { console.log(err); throw err; });
  }

  async retract(id: string, clientOrganisationIds: string[]): Promise<ILibraryRetractResult> {
    return this.http.post(`${BASE}/question-groups/${id}/retract`, { clientOrganisationIds })
      .then(r => r.data).catch(err => { console.log(err); throw err; });
  }

  async setClientEditPermission(id: string, allowClientEdit: boolean): Promise<ILibraryQuestionGroup> {
    return this.http.patch(`${BASE}/question-groups/${id}/client-edit`, { allowClientEdit })
      .then(r => r.data).catch(err => { console.log(err); throw err; });
  }

  // Global lookup, served from the library router so it is not behind the partner-org
  // subscription and role guards that GET /survey-question-type carries.
  async getQuestionTypes(): Promise<any[]> {
    return this.http.get(`${BASE}/question-types`)
      .then(r => r.data).catch(err => { console.log(err); throw err; });
  }

  // ---- the partner's question bank -------------------------------------------

  async getAllQuestions(promptIncludes?: string): Promise<ILibraryQuestion[]> {
    const params = promptIncludes ? `?promptIncludes=${encodeURIComponent(promptIncludes)}` : '';
    return this.http.get(`${BASE}/questions${params}`)
      .then(r => r.data).catch(err => { console.log(err); throw err; });
  }

  async createQuestion(question: Partial<ILibraryQuestion> & { options?: any[] }): Promise<ILibraryQuestion> {
    return this.http.post(`${BASE}/questions`, question)
      .then(r => r.data).catch(err => { console.log(err); throw err; });
  }

  async editQuestion(id: string, question: Partial<ILibraryQuestion>): Promise<ILibraryQuestion> {
    return this.http.patch(`${BASE}/questions/${id}`, question)
      .then(r => r.data).catch(err => { console.log(err); throw err; });
  }

  async deleteQuestion(id: string): Promise<{ success: boolean, message: string }> {
    return this.http.delete(`${BASE}/questions/${id}`)
      .then(r => r.data).catch(err => { console.log(err); throw err; });
  }

  // Options are replaced as a set, and an option keeps its id when one is supplied. That id
  // chain is what keeps clients' stored answers and remapped conditions valid across pushes.
  async replaceOptions(id: string, options: any[]): Promise<ILibraryQuestion> {
    return this.http.put(`${BASE}/questions/${id}/options`, { options })
      .then(r => r.data).catch(err => { console.log(err); throw err; });
  }

  // Per-option add/delete, matching the shape the shared question dialog uses.
  async addOption(questionId: string, option: any) {
    return this.http.post(`${BASE}/questions/${questionId}/options`, option)
      .then(r => r.data).catch(err => { console.log(err); throw err; });
  }

  async deleteOption(questionId: string, optionId: string) {
    return this.http.delete(`${BASE}/questions/${questionId}/options/${optionId}`)
      .then(r => r.data).catch(err => { console.log(err); throw err; });
  }

  // ---- items and emission rules ----------------------------------------------

  async addItem(groupId: string, item: any) {
    return this.http.post(`${BASE}/question-groups/${groupId}/items`, item)
      .then(r => r.data).catch(err => { console.log(err); throw err; });
  }

  async editItem(groupId: string, itemId: string, item: any) {
    return this.http.patch(`${BASE}/question-groups/${groupId}/items/${itemId}`, item)
      .then(r => r.data).catch(err => { console.log(err); throw err; });
  }

  // Bulk page renumber. A page break shifts every item after it, so this is one call and one
  // transaction rather than N.
  async updateItemPages(groupId: string, updates: { id: string, pageNumber: number }[]) {
    return this.http.post(`${BASE}/question-groups/${groupId}/items/update-pages`, { updates })
      .then(r => r.data).catch(err => { console.log(err); throw err; });
  }

  async removeItem(groupId: string, itemId: string) {
    return this.http.delete(`${BASE}/question-groups/${groupId}/items/${itemId}`)
      .then(r => r.data).catch(err => { console.log(err); throw err; });
  }

  async createRule(groupId: string, rule: any) {
    return this.http.post(`${BASE}/question-groups/${groupId}/emission-rules`, rule)
      .then(r => r.data).catch(err => { console.log(err); throw err; });
  }

  async editRule(groupId: string, ruleId: string, rule: any) {
    return this.http.patch(`${BASE}/question-groups/${groupId}/emission-rules/${ruleId}`, rule)
      .then(r => r.data).catch(err => { console.log(err); throw err; });
  }

  async removeRule(groupId: string, ruleId: string) {
    return this.http.delete(`${BASE}/question-groups/${groupId}/emission-rules/${ruleId}`)
      .then(r => r.data).catch(err => { console.log(err); throw err; });
  }
}
